import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Typography,
  Tooltip,
  Button,
  Dialog,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useSelector } from 'react-redux';

import { selectAlertId } from 'src/app/selectors/filterSelectors';
import { selectUserProfile, selectReport, selectAlerts } from 'src/app/selectors/appDataSelectors';
import { Switch } from '@mui/material';
import { updateAlertStatusToFirebase, alertStatusChangeLogs } from 'src/slices/appDataSlice';
import { useDispatch } from 'react-redux';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useConfig } from 'src/context/ConfigContext';

const AlertDetailsCard = () => {
  const [expandedAlerts, setExpandedAlerts] = useState<Record<string, boolean>>(() => {
    const stored = localStorage.getItem('alertDetailsExpanded');
    return stored ? JSON.parse(stored) : {};
  });

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [expandedRules, setExpandedRules] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);

  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    alertId: string | null;
    newStatus: number;
  }>({ open: false, alertId: null, newStatus: 0 });

  const { config } = useConfig();
  const toggleExpand = (id: string) => {
    const updated = {
      ...expandedAlerts,
      [id]: !expandedAlerts[id],
    };
    setExpandedAlerts(updated);
    localStorage.setItem('alertDetailsExpanded', JSON.stringify(updated));
  };

  const toggleSection = (key: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleRule = (key: string) => {
    setExpandedRules(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const dispatch = useDispatch()
  const { alertIds, alertDetails, userProfile, selectedMenu } = useSelector(state => ({
    alertIds: selectAlertId(state),
    alertDetails: selectAlerts(state),
    userProfile: selectUserProfile(state),
    selectedMenu: selectReport(state),
  }));

  const idsToRender: string[] = useMemo(() => {
    if (!alertIds) return [];
    if (Array.isArray(alertIds)) return alertIds;
    if (typeof alertIds === 'string') {
      return alertIds.split(',').map(id => id.trim()).filter(Boolean);
    }
    return [];
  }, [alertIds]);

  const normalizedAlerts = useMemo(() => {
    const original = alertDetails?.alerts || {};
    const transformed: Record<string, any> = {};
  
    Object.values(original).forEach((alert: any) => {
      if (alert?.alert_id != null) {
        transformed[String(alert.alert_id)] = alert;
      }
    });
  
    return transformed;
  }, [alertDetails]);

  if (!alertDetails?.alerts || idsToRender.length === 0) {
    return <Typography variant="body2">No alerts to display.</Typography>;
  }

  // Helper function to format brand names (remove 'ZOOM_' and replace underscores with spaces)
  const formatBrandName = (name: string): string => {
    return name
      .replace(/^ZOOM_/i, '')
      .replace(/_/g, ' ')
      //.toLowerCase()
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  const handleToggleEnabled = (id: string) => {
    // const current = alertDetails.alerts[id]?.enabled;
    const current = normalizedAlerts[id]?.enabled;
    const newStatus = current === 1 ? 0 : 1;
    setConfirmDialog({ open: true, alertId: id, newStatus });
  };

  const confirmToggle = async () => {
    setLoading(true);
    if (!confirmDialog.alertId) return;
  
    if (!config) {
      console.error('No Config loaded');
      return;
    }
  
    const headers = {
      Authorization: config['HEADER']['Authorization'],
    };
  
    // const alert = alertDetails.alerts[confirmDialog.alertId];
    const alert = normalizedAlerts[confirmDialog.alertId];
    const alertId = alert.alert_id;
    const alertType = alert.action;
    const alertStatus = confirmDialog.newStatus ? 'Enabled' : 'Disabled';
    const timeStamp = new Date().toISOString();
    const userName = userProfile?.displayName || 'Unknown';
    const userEmail = userProfile?.email || 'Unknown';

    const originalAlerts = alertDetails.alerts || {};
    const alertkey = Object.keys(originalAlerts).find(
     key => originalAlerts[key]?.alert_id === alert?.alert_id
    );
    try {
      // Wait for Firebase update before logging
      await dispatch(
        updateAlertStatusToFirebase({
          // id: confirmDialog.alertId,
          id: alertkey,
          data: confirmDialog.newStatus,
        }),
      ).unwrap(); // Optional: unwrap to throw if it fails
  
      // Log the status change only after Firebase update
      await dispatch(
        alertStatusChangeLogs({
          url: config.API_URL.alert_status_logs,
          headers,
          alertId,
          alertType,
          alertStatus,
          userName,
          userEmail,
          timeStamp,
        }),
      );
  
      setExpandedAlerts({ ...expandedAlerts });

      setStatusMessage(`Alert ${confirmDialog.newStatus === 1 ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error during alert update or logging:', error);
    } finally {
      setLoading(false);
      setTimeout(() => {
        setConfirmDialog({ open: false, alertId: null, newStatus: 0 });
      }, 1000);
  
      setTimeout(() => {
        setStatusMessage(null);
      }, 1000);
    }
  };  

  return (
    <Box sx={{ position: 'sticky', top: 10, zIndex: 1 }}>
      {idsToRender.map((id: string) => {
        // const alert = alertDetails.alerts[id];
        const alert = normalizedAlerts[id];
        if (!alert) return null;

        return (
          <Card key={id} variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="left" mb={1} padding="8px">
                <Typography variant="subtitle1" fontWeight="bold" color="#ff4500">
                  Alert Id: {alert.alert_id}
                </Typography>
                <Tooltip title={expandedAlerts[id] ? 'Collapse' : 'Expand'} arrow>
                  <IconButton onClick={() => toggleExpand(id)} size="small">
                    {expandedAlerts[id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Tooltip>
              </Box>

              <Collapse in={expandedAlerts[id]} timeout="auto" unmountOnExit>
                <Box display="flex" flexDirection="column" gap={1} pb={1}>
                  <Row label="Ticket Type" value={alert.action} />
                  <Row label="Priority" value={alert.priority} />
                  <Row label="Alert Desc" value={alert.description} />

                  {selectedMenu === 'Alert-Rules' && userProfile?.dash?.superAdmin ? (
                    <Box display="flex" gap={1} alignItems="center">
                       <Typography variant="body2" sx={{ minWidth: 120 }} fontWeight="bold">
                         Enabled:
                      </Typography>
                      <Switch
                        size="small"
                        checked={alert.enabled === 1}
                        onChange={() => handleToggleEnabled(id)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#4caf50',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#4caf50',
                          },
                          '& .MuiSwitch-switchBase': {
                            color: '#808080',
                          },
                          '& .MuiSwitch-track': {
                            backgroundColor: '#808080',
                          },
                        }}
                      />
                     <Typography variant="body2">{alert.enabled === 1 ? 'Yes' : 'No'}</Typography>
                   </Box>
                   ) : (
                   <Row label="Enabled" value={alert.enabled === 1 ? 'Yes' : 'No'} />
                   )}

                  {/* Brands Section */}
                  <Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => toggleSection(`brands-${id}`)}
                      sx={{ textTransform: 'none', mb: 1 }}
                    >
                      {expandedSections[`brands-${id}`] ? 'Hide' : 'Show'} Brands
                    </Button>
                    <Collapse in={expandedSections[`brands-${id}`]}>
                      {/* <Box pl={0}> */}
                      <Box
                        sx={{
                        border: '1px solid #ccc',
                        borderRadius: '0px',
                        padding: '12px',
                        backgroundColor: '#f9f9f9',
                        }}
                       >
                        {Array.isArray(alert.db_name) ? (
                          alert.db_name.map((name, idx) => (
                            <Typography key={idx} variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                              {formatBrandName(name)}
                            </Typography>
                          ))
                        ) : (
                          <Typography variant="body2">-</Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Box>

                  {/* Message Section */}
                  <Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => toggleSection(`message-${id}`)}
                      sx={{ textTransform: 'none', mb: 1 }}
                    >
                      {expandedSections[`message-${id}`] ? 'Hide' : 'Show'} Message
                    </Button>
                    <Collapse in={expandedSections[`message-${id}`]}>
                      <Box
                        sx={{
                        border: '1px solid #ccc',
                        borderRadius: '0px',
                        padding: '12px',
                        backgroundColor: '#f9f9f9',
                        }}
                      >
                        <Typography variant="body2" sx={{fontSize: '0.8rem'}}>{alert.message}</Typography>
                      </Box>
                    </Collapse>
                  </Box>

                  {/* Rules Section */}
                  {Array.isArray(alert.conditions) && (
                    <Box>
                      <Typography variant="subtitle2" mt={2} mb={1} fontWeight="bold">
                        Rules:
                      </Typography>
                      {alert.conditions.map((ruleSet: any[], ruleIdx: number) => {
                        const ruleKey = `rule-${id}-${ruleIdx}`;
                        return (
                          <Box key={ruleKey} mb={2}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => toggleRule(ruleKey)}
                              sx={{ textTransform: 'none', mb: 1 }}
                            >
                              {expandedRules[ruleKey] ? 'Hide' : 'Show'} Rule {ruleIdx + 1}
                            </Button>
                            <Collapse in={expandedRules[ruleKey]}>
                              <Box pl={0}>
                                <Box
                                  component="table"
                                  sx={{
                                    borderCollapse: 'collapse',
                                    // width: '100%',
                                    width: '50%',
                                    '& td, & th': { border: '1px solid #ccc', wordWrap: 'break-word', padding: '2px 2px' },
                                    '& th': { backgroundColor: '#f5f5f5' },
                                    fontSize: '0.7rem',
                                    '& td': {
                                        maxWidth: '80px',
                                        whiteSpace: 'normal',
                                        wordWrap: 'break-word',
                                      },
                                  }}
                                >
                                  <thead>
                                    <tr>
                                      <th>Entity/Field</th>
                                      <th>Operator</th>
                                      <th>Value</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {ruleSet.map((conditionObj, idx) => {
                                      const [entity, fields] = Object.entries(conditionObj)[0];
                                      const [field, ops] = Object.entries(fields)[0];
                                      const [operator, value] = Object.entries(ops)[0];

                                      const operatorColor = {
                                        equal: 'green',
                                        'not equal': 'red',
                                        in: 'blue',
                                        like: 'orange',
                                        'not like': 'crimson',
                                        exist: 'teal',
                                        'not exist': 'gray',
                                      }[operator] || 'black';

                                      // Split the value by commas, remove quotes, and format each item on a new line
                                      const formattedValue = String(value)
                                          .replace(/^'|'$/g, '')
                                          .split(',')
                                          .map(item => item.trim().replace(/^'|'$/g, ''))
                                          .join(',\n');

                                      return (
                                        <tr key={idx}>
                                          <td>{entity}{' '}{field}</td>
                                          <td style={{ color: operatorColor, fontWeight: 500 }}>
                                            {operator}
                                          </td>
                                          {/* <td>{String(value).replace(/^'|'$/g, '')}</td> */}
                                          <td style={{ whiteSpace: 'pre-wrap' }}>
                                            {formattedValue}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </Box>
                              </Box>
                            </Collapse>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        );
      })}

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, alertId: null, newStatus: 0 })}
        maxWidth="xs"
        fullWidth
      >
        {statusMessage && (
        <Typography
           variant="subtitle2"
           sx={{ mt: 2, color: '#ff0f0f', fontWeight: 'bold', textAlign: 'center' }}
        >
          {statusMessage}
        </Typography>
        )}
        <Box p={4}
             textAlign="center"
             display="flex"
             flexDirection="column"
             alignItems="center"
             justifyContent="center"
        >
        {confirmDialog.newStatus === 1 ? (
           <InfoOutlinedIcon fontSize="large" color="primary" sx={{ mb: 1 }} />
        ) : (
           <WarningAmberIcon fontSize="large" color="warning" sx={{ mb: 1 }} />
         )}

           <Typography variant="h6" fontWeight="bold" gutterBottom>
             {confirmDialog.newStatus === 1 ? 'Enable Alert' : 'Disable Alert'}
           </Typography>

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Alert ID: <strong>{confirmDialog.alertId}</strong>
          </Typography>

          <Typography variant="body2" sx={{ mt: 1 }}>
          Are you sure you want to {confirmDialog.newStatus === 1 ? 'enable' : 'disable'} this alert?
          </Typography>

         <Box display="flex" justifyContent="center" gap={2} mt={3}>
         <Button
           variant="outlined"
           onClick={() => setConfirmDialog({ open: false, alertId: null, newStatus: 0 })}
         >
         Cancel
        </Button>
        <Button
          variant="contained"
          color={confirmDialog.newStatus === 1 ? 'primary' : 'warning'}
          onClick={confirmToggle}
        >
          Confirm
        </Button>
       </Box>

       {loading && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Box
            sx={{
               width: 24,
               height: 24,
               border: '3px solid #ed6c02',
               borderTop: '3px solid transparent',
               borderRadius: '50%',
               animation: 'spin 1s linear infinite',
            }}
          />
        </Box>
       )}
     </Box>
    </Dialog>
    </Box>
  );
};

const Row = ({ label, value }: { label: string; value: string | number }) => (
  <Box display="flex" gap={1}>
    <Typography variant="body2" sx={{ minWidth: 120 }} fontWeight="bold">
      {label}:
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

export default AlertDetailsCard;